import type { Price } from '@misumi/shared'
import type {
  DfmSummary,
  LibraryPartRow,
  LibraryRowStatus,
  PartFilterStatus,
  PricingProjection,
} from '../types'

/**
 * UID-434/WS-A — the narrow, vertical-agnostic view model
 * `PartConfiguratorShell` (the extraction of the old `EditConfigurationModal`
 * body) reads instead of the heavy Parts-vertical `LibraryPartRow`. Neither
 * an RFQ line (`RfqReviewLine`, `verticals/rfq/review-domain.ts`) nor a Cart
 * line can supply a `LibraryPartRow` — they don't carry a
 * `dfmSummary`/`pricingProjection`/`indicativePrice` the same way, and the
 * PATCH target the old modal read off `sourceable.id` isn't even the right
 * concept for those two (RFQ doesn't PATCH a part at all, ADR-0117 D9.1's
 * "each entry point keeps its own seam" ruling). This shape carries only
 * what the shell's PRESENTATION needs — identity, display text, the seeded
 * config attributes, and OPTIONAL DFM/pricing/scene inputs — so a caller
 * with none of the optional fields still renders a complete, honest modal
 * (empty states, never fabricated data — see the shell's tab bodies).
 *
 * `fromLibraryPartRow` below is the ONLY adapter that exists today; it is a
 * pure, lossless-for-the-shell projection so the Part Library call sites
 * change as little as possible (EXPAND, don't rewrite). A future RFQ/Cart
 * adapter (`fromRfqReviewLine`/`fromCartLine`, out of scope for this
 * foundation story) would live beside it, mapping their own aggregates onto
 * the SAME `PartConfiguratorSubject` shape.
 */
export interface PartConfiguratorSubject {
  /** Identity only — the shell never assumes this is a PATCH-able server
   * part id; each entry point's own save seam decides what (if anything) it
   * targets with it. Also this modal's React `key`/dependency-array anchor
   * (mirrors the old modal's `[part]` `useMemo` dependency). */
  readonly id: string
  readonly displayName: string
  readonly thumbnailUrl: string | null
  readonly drawingFilename: string | null
  /** Seeds every Configuration-tab form field (`readStringAttr`/
   * `readNumberAttr`/`readStringListAttr` against this bag) — absent for a
   * subject with nothing configured yet (every field falls back to its own
   * default, same as today). */
  readonly configAttributes: Readonly<Record<string, unknown>> | undefined
  readonly quantity: number
  /** Present only when this entry point's aggregate carries a real DFM
   * projection. Absent has TWO distinct honest readings, disambiguated by
   * `PartConfiguratorCapabilities.dfmCapable` (see that type): "not
   * computed YET" (Parts) vs. "not a concept for this entry point" (RFQ/Cart
   * pre-quote) — never conflated into one spinner-or-nothing state. */
  readonly dfmSummary?: DfmSummary
  readonly indicativePrice?: Price
  readonly pricingProjection?: PricingProjection
  readonly rowStatus?: LibraryRowStatus
  readonly filterStatus?: PartFilterStatus
  /** Key into `DEMO_SCENE_BY_PART_NAME` (UID-311) — the viewer's small,
   * explicitly-canned demo-scene allow list is keyed by part NAME today, so
   * this is that name, not a new lookup key. */
  readonly sceneKey?: string
  /** The Drawing-analysis tab's `PartAttachmentsSection` target. Absent for
   * an entry point with no real, addressable part id yet (a pre-checkout
   * Cart line, a pre-quote RFQ line) — the tab then renders an honest empty
   * state instead of mounting that section against a fabricated id. */
  readonly attachmentsPartId?: string
  /** UID-225 — the design mock's viewer panel shows illustrative dimensions
   * ("2.99 x 2.22 x 0.28 in") as static content, but no dimensions field
   * exists anywhere on the contract today for any entry point. Absent here
   * means "no real data to show," so the shell's dimensions row renders
   * nothing rather than a permanent "not available" placeholder or
   * fabricated numbers. Populating this for real requires an upstream
   * contract change (a dimensions field on the part/RFQ-line/cart-line
   * aggregate) and a mapper here — out of scope until that lands. */
  readonly dimensions?: string
}

/**
 * The Part Library adapter (UID-434/WS-A). Lossless: every field the old
 * `EditConfigurationModal` read off `LibraryPartRow` is carried over
 * verbatim, so `EditConfigurationModal.tsx` (now a thin wrapper around
 * `PartConfiguratorShell`) renders byte-for-byte what it did before this
 * extraction — see the characterization suite
 * (`EditConfigurationModal.test.tsx`) for the proof.
 */
export function fromLibraryPartRow(part: LibraryPartRow): PartConfiguratorSubject {
  return {
    id: part.sourceable.id,
    displayName: part.sourceable.name ?? 'part',
    thumbnailUrl: part.thumbnailUrl,
    drawingFilename: part.drawingFilename,
    configAttributes: part.sourceable.configAttributes,
    quantity: part.quantity,
    ...(part.sourceable.dfmSummary ? { dfmSummary: part.sourceable.dfmSummary } : {}),
    ...(part.sourceable.indicativePrice
      ? { indicativePrice: part.sourceable.indicativePrice }
      : {}),
    ...(part.sourceable.pricingProjection
      ? { pricingProjection: part.sourceable.pricingProjection }
      : {}),
    ...(part.rowStatus ? { rowStatus: part.rowStatus } : {}),
    ...(part.filterStatus ? { filterStatus: part.filterStatus } : {}),
    ...(part.sourceable.name ? { sceneKey: part.sourceable.name } : {}),
    attachmentsPartId: part.sourceable.id,
  }
}

/**
 * Capability/scoping flags — which sections/controls this entry point can
 * ever show real data or affordances for. Distinct from the per-instance
 * OPTIONAL DATA on `PartConfiguratorSubject` above (e.g. `dfmSummary`
 * absent-this-time vs. `dfmCapable` absent-ALWAYS-for-this-entry-point) —
 * see each field's doc comment for the distinction it draws.
 *
 * Only ONE shape exists today (`PART_LIBRARY_CAPABILITIES`, Parts' own
 * unchanged behavior). This module is the single, module-scoped seam a
 * future release flag or a real RFQ/Cart capability set would extend at —
 * per the ticket's "one seam, not scattered" instruction — rather than a
 * flag check appearing at each of the shell's call sites.
 */
export interface PartConfiguratorCapabilities {
  /** UID-46 "Apply to multiple parts" — Part Library ONLY. Per the human
   * D-ruling for this extraction, RFQ/Cart entry points never render this
   * control AT ALL (not even disabled) — see `PartConfiguratorShell`'s
   * left-column-actions render branch. */
  readonly applyToMultiple: boolean
  /** Whether the Manufacturability-feedback tab can EVER have real
   * `dfmSummary` data for this entry point (not whether it does THIS time —
   * that's `subject.dfmSummary`'s own presence). `true` (Parts): an absent
   * `dfmSummary` reads as "still in progress" (a real async analysis that
   * hasn't completed yet). `false`: an absent `dfmSummary` reads as "not
   * available for this part" — never a fabricated "in progress" spinner for
   * an entry point where DFM will never be computed (human ruling D3). */
  readonly dfmCapable: boolean
  /** Whether the Drawing-analysis tab has a real, addressable part id to
   * mount `PartAttachmentsSection` against THIS time — mirrors
   * `subject.attachmentsPartId`'s own presence; kept as a capability (not
   * inferred solely from the subject) so a future entry point that is
   * NEVER attachment-capable can say so explicitly rather than relying on
   * every one of its subjects happening to omit the id. */
  readonly attachmentsCapable: boolean
  /** UID-434/WS-D — whether the Finish section's Color sub-select can ever
   * PERSIST for this entry point. `EditPartConfigRequest` carries `color`;
   * `RfqLineSpecification` (`apps/api/openapi.dist.yaml:4030`) does not — so
   * RFQ must never render an editable color control it would silently
   * discard on save (the exact UID-403 bug 3 shape this ticket exists to not
   * reintroduce). `false` for an entry point: the Color control does not
   * render AT ALL, even when the chosen finish would otherwise offer colors —
   * never a concept for this entry point, distinct from "no color chosen
   * yet." */
  readonly colorCapable: boolean
  /** Whether the Threaded holes section can ever PERSIST for this entry
   * point. `EditPartConfigRequest` carries `threadedHoleCount`;
   * `RfqLineSpecification` does not. `false`: the whole section is omitted,
   * not rendered read-only — there is no stored value to reflect and no
   * server field to save one into. */
  readonly threadedHolesCapable: boolean
  /** Whether the Additional requirements section can ever PERSIST for this
   * entry point. `EditPartConfigRequest` carries `additionalRequirements`;
   * `RfqLineSpecification` does not. `false`: the section is omitted. */
  readonly additionalRequirementsCapable: boolean
  /** RFQ-ONLY, the mirror image of the three flags above: whether the
   * Inspections section offers the "Custom inspection" routing option
   * (`RfqLineSpecification.customInspection`). `EditPartConfigRequest` has NO
   * such field at all (ADR-0117 D9.1) — Part Library's capability set keeps
   * this `false` because there is nowhere for a `true` value to be saved, not
   * because the option was overlooked. */
  readonly customInspectionCapable: boolean
  /** UID-434 human ruling (post-launch copy fix) — which copy/CTA the
   * shell's BUILT-IN `ConfigPricingFooter` renders for its non-locked `'rfq'`
   * footer state (`resolvePricingFooterState` returning `'rfq'` with
   * `projection?.mode !== 'locked_to_rfq'`). This is an entry-point-scoped
   * PRESENTATION choice, not persistable data, so it lives here rather than
   * on `PartConfiguratorSubject` — every subject from one entry point reads
   * the same way, exactly like the `xCapable` flags above.
   *
   * `'manualQuote'` (Parts' unchanged behavior; also what RFQ's OWN
   * `RfqLineConfiguratorPricingFooter` renders for the equivalent case,
   * kept byte-identical there but via that separate component, never this
   * seam): the config genuinely includes options that need a human to price
   * it, so the "Send to RFQ" CTA is offered alongside the explanation.
   *
   * `'cartLineReset'` (Cart only): a Cart line lands in this footer state
   * because the buyer just edited its configuration and the server cleared
   * the line's price/delivery promise for the OLD configuration — not
   * because this configuration requires a manual quote, and nothing here
   * automatically hands the line off to RFQ. The copy names that real cause
   * instead, and the "Send to RFQ" CTA is omitted entirely (see
   * `ConfigPricingFooter`'s render for the reasoning: showing it — even
   * disabled — would imply an automatic RFQ hand-off that doesn't happen,
   * and Cart has no wired "send this line to RFQ" action to attach in its
   * place). */
  readonly rfqFooterCopyKind: 'manualQuote' | 'cartLineReset'
}

/** Part Library's own capability set — unchanged from the pre-extraction
 * modal's behavior: "Apply to multiple parts" renders (per the
 * `onApplyToMultiple` optional-callback-disabled-with-title pattern), DFM
 * absence reads as "still in progress", and the Drawing-analysis tab always
 * has a real part id. Every `EditPartConfigRequest` field this shell can
 * render a control for (color, threaded holes, additional requirements) is
 * persistable here, and RFQ's `customInspection` routing state has no
 * `EditPartConfigRequest` counterpart to save into. */
export const PART_LIBRARY_CAPABILITIES: PartConfiguratorCapabilities = {
  applyToMultiple: true,
  dfmCapable: true,
  attachmentsCapable: true,
  colorCapable: true,
  threadedHolesCapable: true,
  additionalRequirementsCapable: true,
  customInspectionCapable: false,
  rfqFooterCopyKind: 'manualQuote',
}
